// SCR-RPT-02 (frozen mockups-rpt-v1): the owner's reports (ADR-126) with period, truck, Seller, product and customer
// filters (ADR-127) and the sales basis; tabs Sales · Debt · Rounds · Expenses (ADR-087). On phones the filters are one
// line that opens a sheet; on desktop they show in full. The totals are the dashboard's for the same scope (REP-05).
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BottomSheet,
  Button,
  Chips,
  DataTable,
  EmptyState,
  Field,
  Filters,
  FormGrid,
  Input,
  QuickActions,
  Select,
  SheetActions,
  SkeletonList,
  Tabs,
  useWide,
} from '../../design/kit';
import { REPORTS, loadExpensesReport, loadRoundsReport, loadSalesReport, type ReportFilters } from '../../data/reports';
import { loadDebtReport } from '../../data/reports';
import { loadUsers } from '../../data/settings';
import { Code, Name } from '../../i18n/Isolate';
import { saveCsv } from '../../lib/ux/csv';
import { categoryLabel } from '../../data/expenses';
import { businessToday } from '../../lib/format';
import { useTrucks } from '../rounds/TrucksPage';
import { useCatalogData, useCustomers, useTab } from '../catalog/common';
import { BasisChips, DebtTable, useReportWords } from './DashboardPage';

const TABS = ['rsales', 'rdebt', 'rrounds', 'rexp'] as const;
type Tab = (typeof TABS)[number];
type Group = 'product' | 'seller' | 'customer';
const monthStart = (d: string) => `${d.slice(0, 8)}01`;
const EMPTY = (): ReportFilters => {
  const today = businessToday();
  return {
    from: monthStart(today),
    to: today,
    basis: 'sale',
    truck: null,
    seller: null,
    product: null,
    customer: null,
  };
};

export function ReportsPage() {
  const { t, lang, day } = useReportWords();
  const wide = useWide();
  const [tab, setTab] = useTab(TABS, 'rsales');
  const [filters, setFilters] = useState<ReportFilters>(EMPTY);
  const [group, setGroup] = useState<Group>('product');
  const [sheet, setSheet] = useState(false);
  const [csv, setCsv] = useState<{ head: string[]; lines: unknown[][] } | null>(null);
  const count = [filters.truck, filters.seller, filters.product, filters.customer].filter(Boolean).length;
  const clear = () => setFilters(EMPTY());
  return (
    <>
      {wide ? (
        <Filters>
          <FilterFields value={filters} onChange={setFilters} />
          <BasisChips value={filters.basis} onChange={(basis) => setFilters({ ...filters, basis })} />
        </Filters>
      ) : (
        <>
          <Chips
            label={t('rp.filters')}
            items={[
              { id: 'period', label: `${day(filters.from)} – ${day(filters.to)}` },
              { id: 'filters', label: count ? `${t('rp.filters')} · ${count}` : t('rp.filters') },
            ]}
            selected={count ? ['filters'] : []}
            onToggle={() => setSheet(true)}
          />
          <BasisChips value={filters.basis} onChange={(basis) => setFilters({ ...filters, basis })} />
        </>
      )}
      <QuickActions
        actions={[
          {
            id: 'csv',
            label: t('rp.export'),
            icon: 'download',
            onSelect: () => csv && saveCsv(`yakvender-${tab}`, csv.head, csv.lines, lang),
          },
        ]}
      />
      <Tabs
        label={t('p.reports')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
      />
      {tab === 'rsales' && (
        <SalesTab filters={filters} group={group} onGroup={setGroup} onCsv={setCsv} onClear={clear} />
      )}
      {tab === 'rdebt' && <DebtTab filters={filters} onCsv={setCsv} onClear={clear} />}
      {tab === 'rrounds' && <RoundsTab filters={filters} onCsv={setCsv} onClear={clear} />}
      {tab === 'rexp' && <ExpensesTab filters={filters} onCsv={setCsv} onClear={clear} />}
      {sheet && (
        <FilterSheet
          value={filters}
          onClose={() => setSheet(false)}
          onApply={(f) => {
            setFilters(f);
            setSheet(false);
          }}
        />
      )}
    </>
  );
}

function FilterFields({ value, onChange }: { value: ReportFilters; onChange: (f: ReportFilters) => void }) {
  const { t } = useReportWords();
  const trucks = useTrucks();
  const users = useQuery({ queryKey: ['settings', 'users'], queryFn: loadUsers });
  const { products } = useCatalogData();
  const customers = useCustomers();
  const pick = (
    label: string,
    current: string | null,
    set: (v: string | null) => void,
    options: { id: string; name: string }[],
  ) => (
    <Field label={label}>
      {({ id }) => (
        <Select id={id} value={current ?? ''} onChange={(e) => set(e.target.value || null)}>
          <option value="">{t('rp.all')}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
  return (
    <>
      <FormGrid>
        <Field label={t('rp.from')}>
          {({ id }) => (
            <Input
              id={id}
              type="date"
              dir="ltr"
              value={value.from}
              onChange={(e) => onChange({ ...value, from: e.target.value })}
            />
          )}
        </Field>
        <Field label={t('rp.to')}>
          {({ id }) => (
            <Input
              id={id}
              type="date"
              dir="ltr"
              value={value.to}
              onChange={(e) => onChange({ ...value, to: e.target.value })}
            />
          )}
        </Field>
      </FormGrid>
      <FormGrid>
        {pick(
          t('rp.truck'),
          value.truck,
          (truck) => onChange({ ...value, truck }),
          (trucks.data ?? []).map((x) => ({ id: x.id, name: x.code })),
        )}
        {pick(
          t('rp.seller'),
          value.seller,
          (seller) => onChange({ ...value, seller }),
          (users.data ?? [])
            .filter((u) => u.role === 'seller' || u.role === 'owner')
            .map((u) => ({ id: u.user_id, name: u.display_name })),
        )}
      </FormGrid>
      <FormGrid>
        {pick(
          t('rp.product'),
          value.product,
          (product) => onChange({ ...value, product }),
          (products.data ?? []).map((p) => ({ id: p.id, name: p.name })),
        )}
        {pick(
          t('rp.customer'),
          value.customer,
          (customer) => onChange({ ...value, customer }),
          (customers.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        )}
      </FormGrid>
    </>
  );
}

function FilterSheet({
  value,
  onClose,
  onApply,
}: {
  value: ReportFilters;
  onClose: () => void;
  onApply: (f: ReportFilters) => void;
}) {
  const { t } = useReportWords();
  const [draft, setDraft] = useState(value);
  return (
    <BottomSheet title={t('rp.filters')} open onClose={onClose}>
      <div className="yv-skeleton-stack">
        <FilterFields value={draft} onChange={setDraft} />
        <SheetActions>
          <Button onClick={() => setDraft({ ...EMPTY(), basis: draft.basis })}>{t('rp.clear')}</Button>
          <Button variant="main" onClick={() => onApply(draft)}>
            {t('rp.apply')}
          </Button>
        </SheetActions>
      </div>
    </BottomSheet>
  );
}

type TabProps = {
  filters: ReportFilters;
  onCsv: (c: { head: string[]; lines: unknown[][] }) => void;
  onClear: () => void;
};

function Empty({ onClear }: { onClear: () => void }) {
  const { t } = useReportWords();
  return (
    <EmptyState
      icon="search"
      title={t('rp.empty')}
      text={t('rp.empty.text')}
      action={<Button onClick={onClear}>{t('rp.clear')}</Button>}
    />
  );
}
function useReport<T>(key: unknown[], fn: () => Promise<T[]>) {
  return useQuery({ queryKey: [...REPORTS, ...key], queryFn: fn, retry: false });
}
const pct = (margin: number, net: number) => (net ? `${Math.round((100 * margin) / net)}%` : '—');

function SalesTab({
  filters,
  group,
  onGroup,
  onCsv,
  onClear,
}: TabProps & { group: Group; onGroup: (g: Group) => void }) {
  const { t, money } = useReportWords();
  const rows = useReport(['sales', group, filters], () => loadSalesReport(filters, group));
  const head = [
    t('col.name'),
    t('col.pieces'),
    t('col.count'),
    t('col.net'),
    t('col.cost'),
    t('col.margin'),
    t('col.pct'),
  ];
  const list = rows.data ?? [];
  useCsvRows(
    onCsv,
    head,
    rows.data?.map((r) => [r.name, r.pieces, r.sales, r.net, r.cost, r.margin, pct(r.margin, r.net)]),
  );
  const sum = (k: 'pieces' | 'sales' | 'net' | 'cost' | 'margin') => list.reduce((s, r) => s + r[k], 0);
  return (
    <>
      <Chips
        label={t('tab.rsales')}
        items={[
          { id: 'product', label: t('rp.byproduct') },
          { id: 'seller', label: t('rp.byseller') },
          { id: 'customer', label: t('rp.bycustomer') },
        ]}
        selected={[group]}
        onToggle={(id) => onGroup(id as Group)}
      />
      {!rows.data ? (
        <SkeletonList label={t('loading')} />
      ) : list.length === 0 ? (
        <Empty onClear={onClear} />
      ) : (
        <>
          <DataTable
            label={t('tab.rsales')}
            columns={head.map((label, i) => ({ key: String(i), label, num: i > 0 }))}
            rows={list.map((r) => ({
              key: r.id ?? r.name,
              cells: [
                <Name>{r.name}</Name>,
                String(r.pieces),
                String(r.sales),
                money(r.net),
                money(r.cost),
                money(r.margin),
                pct(r.margin, r.net),
              ],
            }))}
            total={[
              t('rp.total'),
              String(sum('pieces')),
              String(sum('sales')),
              money(sum('net')),
              money(sum('cost')),
              money(sum('margin')),
              pct(sum('margin'), sum('net')),
            ]}
          />
          <p className="muted">{t('rp.same')}</p>
        </>
      )}
    </>
  );
}

function DebtTab({ filters, onCsv, onClear }: TabProps) {
  const { t } = useReportWords();
  const rows = useReport(['debt', filters.seller, filters.customer], () => loadDebtReport(filters));
  useCsvRows(
    onCsv,
    [t('rp.customer'), t('ag.due'), t('ag.30'), t('ag.over'), t('ag.total')],
    rows.data?.map((r) => [r.name, r.due, r.up_to_30, r.over_30, r.total]),
  );
  if (!rows.data) return <SkeletonList label={t('loading')} />;
  return rows.data.length === 0 ? <Empty onClear={onClear} /> : <DebtTable rows={rows.data} />;
}

function RoundsTab({ filters, onCsv, onClear }: TabProps) {
  const { t, money, day } = useReportWords();
  const rows = useReport(['rounds', filters], () => loadRoundsReport(filters));
  const head = [
    t('col.round'),
    t('col.date'),
    t('col.seller'),
    t('col.net'),
    t('col.cost'),
    t('col.margin'),
    t('col.exp'),
    t('col.countdiff'),
    t('col.variance'),
    t('col.status'),
  ];
  const status = (s: string) => t(s === 'closed' ? 'st.closed' : s === 'open' ? 'st.open' : 'st.ended');
  useCsvRows(
    onCsv,
    head,
    rows.data?.map((r) => [
      r.number,
      r.day,
      r.seller,
      r.net,
      r.cost,
      r.margin,
      r.expenses,
      r.count_difference,
      r.variance,
      status(r.status),
    ]),
  );
  if (!rows.data) return <SkeletonList label={t('loading')} />;
  if (rows.data.length === 0) return <Empty onClear={onClear} />;
  return (
    <DataTable
      label={t('tab.rrounds')}
      columns={head.map((label, i) => ({ key: String(i), label, num: i >= 3 && i <= 8 }))}
      rows={rows.data.map((r) => ({
        key: r.round_id,
        cells: [
          <Code>{r.number}</Code>,
          <bdi className="yv-nowrap">{day(r.day)}</bdi>,
          r.seller ? <Name>{r.seller}</Name> : '—',
          money(r.net),
          money(r.cost),
          money(r.margin),
          money(r.expenses),
          money(r.count_difference),
          r.variance === null ? '—' : money(r.variance),
          status(r.status),
        ] as ReactNode[],
      }))}
      warn={(row, col) => col === '8' && (rows.data?.find((r) => r.round_id === row)?.variance ?? 0) < 0}
    />
  );
}

function ExpensesTab({ filters, onCsv, onClear }: TabProps) {
  const { t, money } = useReportWords();
  const navigate = useNavigate();
  const rows = useReport(['expenses', filters.from, filters.to, filters.truck], () => loadExpensesReport(filters));
  // The fixed wage category reads in the user's language (P7 view labels).
  const cat = (name: string, fixed: boolean) => categoryLabel(t, name, fixed ? 'wage' : null);
  useCsvRows(
    onCsv,
    [t('col.cat'), t('col.n'), t('col.total')],
    rows.data?.map((r) => [cat(r.category, r.fixed), r.n, r.total]),
  );
  if (!rows.data) return <SkeletonList label={t('loading')} />;
  if (rows.data.length === 0) return <Empty onClear={onClear} />;
  return (
    <>
      <DataTable
        label={t('tab.rexp')}
        columns={[
          { key: 'cat', label: t('col.cat') },
          { key: 'n', label: t('col.n'), num: true },
          { key: 'total', label: t('col.total'), num: true },
        ]}
        rows={rows.data.map((r) => ({
          key: r.category_id,
          cells: [<Name>{cat(r.category, r.fixed)}</Name>, String(r.n), money(r.total)],
        }))}
        total={[
          t('rp.total'),
          String(rows.data.reduce((s, r) => s + r.n, 0)),
          money(rows.data.reduce((s, r) => s + r.total, 0)),
        ]}
      />
      <QuickActions
        actions={[{ id: 'exp', label: t('rp.openexp'), icon: 'file', onSelect: () => navigate('/expenses') }]}
      />
    </>
  );
}

/** Hands the open tab's rows to the export button when they change. */
function useCsvRows(
  onCsv: (c: { head: string[]; lines: unknown[][] }) => void,
  head: string[],
  lines: unknown[][] | undefined,
) {
  const key = lines ? JSON.stringify([head, lines]) : '';
  useEffect(() => {
    if (lines) onCsv({ head, lines });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
